import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const unitMap = (u) => {
  const x = (u || '').toLowerCase().trim();
  if (['lb', 'lbs', 'pound', 'pounds'].includes(x)) return 'lbs';
  if (['ft', 'foot', 'feet', 'lf', 'linear foot'].includes(x)) return 'ft';
  if (['sq ft', 'sqft', 'sq_ft', 'square foot'].includes(x)) return 'sq_ft';
  if (['ton', 'tons'].includes(x)) return 'tons';
  return 'each';
};

/**
 * Material name input with a combined picker:
 *  - Consumables catalog matches (choose Cost or Sell per item)
 *  - Previous pricing matches (most recent + expandable mini-history)
 * Calls onApply(patch) to merge fields into the quote line. Free typing supported.
 */
export const MaterialPicker = ({ value, onApply }) => {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ catalog: [], previous: [] });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!value || value.trim().length < 2) {
      setResults({ catalog: [], previous: [] });
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/materials/lookup`, {
          params: { q: value, limit: 8 },
          headers: { Authorization: `Bearer ${token}` },
        });
        setResults(res.data || { catalog: [], previous: [] });
      } catch (e) {
        setResults({ catalog: [], previous: [] });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pickCatalog = (item, useSell) => {
    onApply({
      material_name: item.name,
      unit: unitMap(item.unit),
      unit_cost: useSell ? item.sell_price : item.unit_cost,
      ...(useSell ? { individual_markup_percentage: 0 } : {}),
    });
    setOpen(false);
  };

  const pickPrevious = (item) => {
    onApply({
      material_name: item.description,
      unit: unitMap(item.unit),
      unit_cost: Number(item.latest_price) || 0,
    });
    setOpen(false);
  };

  const catalog = results.catalog || [];
  const previous = results.previous || [];
  const hasResults = catalog.length > 0 || previous.length > 0;
  const showDropdown = open && value && value.trim().length >= 2;

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <input
          value={value || ''}
          onChange={(e) => { onApply({ material_name: e.target.value }); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search catalog or type..."
          data-testid="material-name-input"
          className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-2 py-1 pr-7 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
        <Search className="h-3.5 w-3.5 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>

      {showDropdown && (
        <div
          className="absolute z-50 mt-1 w-80 max-w-[90vw] overflow-y-auto rounded-md border border-slate-200 bg-white shadow-xl max-h-80"
          data-testid="material-picker-dropdown"
        >
          {loading && <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>}
          {!loading && !hasResults && (
            <div className="px-3 py-2 text-xs text-slate-500">
              No matches — “{value}” will be saved as new pricing when you save the quote.
            </div>
          )}

          {catalog.length > 0 && (
            <div>
              <div className="sticky top-0 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-blue-600">
                Catalog
              </div>
              {catalog.map((item) => (
                <div key={item.id} className="border-b border-slate-100 px-3 py-2 hover:bg-slate-50" data-testid="catalog-result">
                  <button type="button" onClick={() => pickCatalog(item, false)} className="block w-full text-left">
                    <div className="truncate text-sm font-medium text-slate-800">{item.name}</div>
                    <div className="truncate text-[11px] text-slate-500">
                      {item.part_number ? `#${item.part_number} · ` : ''}{item.category}
                    </div>
                  </button>
                  <div className="mt-1 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => pickCatalog(item, false)}
                      className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700 hover:bg-slate-200"
                      data-testid="use-cost-btn"
                    >
                      Cost ${Number(item.unit_cost).toFixed(2)} <span className="text-slate-500">+markup</span>
                    </button>
                    {item.sell_price > 0 && (
                      <button
                        type="button"
                        onClick={() => pickCatalog(item, true)}
                        className="rounded bg-green-100 px-2 py-0.5 text-[11px] text-green-700 hover:bg-green-200"
                        data-testid="use-sell-btn"
                      >
                        Sell ${Number(item.sell_price).toFixed(2)}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {previous.length > 0 && (
            <div>
              <div className="sticky top-0 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-600">
                Previous Pricing
              </div>
              {previous.map((item, idx) => (
                <div key={idx} className="border-b border-slate-100 px-3 py-2 hover:bg-slate-50" data-testid="previous-result">
                  <div className="flex items-start justify-between gap-2">
                    <button type="button" onClick={() => pickPrevious(item)} className="min-w-0 flex-1 text-left">
                      <div className="truncate text-sm font-medium text-slate-800">{item.description}</div>
                      <div className="text-[11px] text-slate-500">
                        ${Number(item.latest_price).toFixed(2)} · entered {fmtDate(item.latest_date)}
                        {item.latest_quote_number ? ` · #${item.latest_quote_number}` : ''}
                      </div>
                    </button>
                    {item.history && item.history.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setExpanded(expanded === idx ? null : idx)}
                        className="text-slate-500 hover:text-slate-600"
                        data-testid="history-toggle"
                      >
                        {expanded === idx ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                  {expanded === idx && item.history && (
                    <div className="mt-1 space-y-0.5 border-l-2 border-amber-200 pl-2">
                      {item.history.slice(0, 3).map((h, hi) => (
                        <div key={hi} className="flex justify-between text-[11px] text-slate-500">
                          <span>${Number(h.price).toFixed(2)} / {h.unit}</span>
                          <span>{fmtDate(h.date)}{h.quote_number ? ` · #${h.quote_number}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MaterialPicker;
